// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Injectable } from '@angular/core';
import { DEFAULT_MAP_ID, GoogleMapsService, ILocation, MapService } from '@ess-front/shared';
import { environment } from '@environment';
import LatLngBounds = google.maps.LatLngBounds;

@Injectable()
export class SelfExploreMapService {
  markerList: { active: boolean; experience: ILocation; marker: google.maps.marker.AdvancedMarkerElement }[] = [];
  map: google.maps.Map | null = null;
  markerBounds: LatLngBounds | null = null;
  markerClickFunction: ((index: number) => void) | null = null;
  isMobile = false;
  private readonly MARKER_URL = environment.markerUrl;
  private readonly ACTIVE_MARKER_URL = environment.markerActiveUrl;

  constructor(
    private googleMapsService: GoogleMapsService,
    private mapService: MapService,
  ) {}

  async createMap(
    mapElement: HTMLElement,
    experiences: ILocation[],
    isMobile: boolean,
    markerClickFunction: ((index: number) => void) | null = null,
    allowGestures = false,
  ) {
    this.isMobile = isMobile;
    this.markerClickFunction = markerClickFunction;
    const start = await this.googleMapsService.getLatLng(
      experiences[0].location.latitude,
      experiences[0].location.longitude,
    );
    const mapOptions = {
      center: start,
      clickableIcons: false,
      disableDefaultUI: true,
      gestureHandling: allowGestures ? 'auto' : 'none',
      mapId: DEFAULT_MAP_ID,
      mapTypeId: await this.googleMapsService.getMapTypeId(),
      zoom: 12,
      zoomControl: false,
    };
    this.map = await this.googleMapsService.getMap(mapElement, mapOptions);
    this.markerBounds = await this.googleMapsService.getLatLngBounds();
    const includeActiveMarker = !!markerClickFunction;
    await this.addMarkers(this.map, experiences, this.markerBounds, includeActiveMarker);
  }

  public async offsetMap(overlayWidth: number) {
    const { x: mapTopRightDistance } = await this.googleMapsService.distanceToTopLeftInPx(
      this.map.getBounds().getNorthEast(),
      this.map,
    );

    const { x: distanceLeftBound } = await this.googleMapsService.distanceToTopLeftInPx(
      this.markerBounds.getSouthWest(),
      this.map,
    );
    const { x: distanceRightBound } = await this.googleMapsService.distanceToTopLeftInPx(
      this.markerBounds.getNorthEast(),
      this.map,
    );

    // Length on px the overlay it's over the bound
    const leftOverlap = overlayWidth - distanceLeftBound;
    // Length on px to spare on the right
    const rightMarginToOverlap = mapTopRightDistance - distanceRightBound;

    // The overlay is over the left bound, potentially hiding markers
    if (this.isOverlapping(leftOverlap)) {
      if (this.isMarginBiggerThanOverlap(leftOverlap, rightMarginToOverlap)) {
        const mapOffset = Math.round((rightMarginToOverlap - leftOverlap) / 2);
        this.map.panBy(-mapOffset, 0);
        if (await this.newPositionOverlaps(overlayWidth, this.markerBounds, this.map)) {
          await this.offsetMap(overlayWidth);
        }
      } else {
        this.map.setZoom(this.map.getZoom() - 1);
        await this.offsetMap(overlayWidth);
      }
    }
  }

  public async offsetMapBottom(overlayHeight: number) {
    const { y: mapBottomDistance } = await this.googleMapsService.distanceToTopLeftInPx(
      this.map.getBounds().getSouthWest(),
      this.map,
    );

    const { y: distanceTopBound } = await this.googleMapsService.distanceToTopLeftInPx(
      this.markerBounds.getNorthEast(),
      this.map,
    );
    const { y: distanceBottomBound } = await this.googleMapsService.distanceToTopLeftInPx(
      this.markerBounds.getSouthWest(),
      this.map,
    );

    // Length on px the overlay it's over the bound
    const bottomOverlap = overlayHeight + distanceBottomBound - mapBottomDistance;
    // Length on px to spare on the top
    const topMarginToOverlap = distanceTopBound;

    // The overlay is over the bottom bound, potentially hiding markers
    if (this.isOverlapping(bottomOverlap)) {
      if (this.isMarginBiggerThanOverlap(bottomOverlap, topMarginToOverlap)) {
        const mapOffset = Math.round((topMarginToOverlap - bottomOverlap) / 2);
        this.map.panBy(0, mapOffset);
        if (await this.newPositionOverlaps(overlayHeight, this.markerBounds, this.map, false)) {
          await this.offsetMapBottom(overlayHeight);
        }
      } else {
        this.map.setZoom(this.map.getZoom() - 1);
        await this.offsetMapBottom(overlayHeight);
      }
    }
  }

  public async changeActiveMarker(index: number) {
    if (this.markerList.length > 0 && !this.markerList[index]?.active) {
      const previousActiveMarkerIndex = this.markerList.findIndex(experienceMarker => experienceMarker.active);
      const previousActiveMarker = this.markerList[previousActiveMarkerIndex];
      previousActiveMarker.marker.map = null;
      const newNormalMarker = await this.createNormalMarker(
        previousActiveMarker.experience,
        previousActiveMarkerIndex,
        this.map,
      );
      this.markerList[previousActiveMarkerIndex] = {
        marker: newNormalMarker,
        active: false,
        experience: previousActiveMarker.experience,
      };

      const previousNormalMarker = this.markerList[index];
      this.markerList[index].marker.map = null;
      const newActiveMarker = await this.createActiveMarker(previousNormalMarker.experience, index, this.map);
      this.markerList[index] = { marker: newActiveMarker, active: true, experience: previousNormalMarker.experience };
    }
  }

  private async addMarkers(
    map: google.maps.Map,
    experiences: ILocation[],
    markerBounds: google.maps.LatLngBounds,
    includeActiveMarker: boolean,
  ) {
    let index = 0;
    for (const experience of experiences) {
      const latLng = await this.googleMapsService.getLatLng(
        experience.location.latitude,
        experience.location.longitude,
      );
      let marker;
      if (index === 0 && includeActiveMarker) {
        marker = await this.createActiveMarker(experience, index, map);
        this.markerList.push({ marker, active: true, experience });
      } else {
        marker = await this.createNormalMarker(experience, index, map);
        this.markerList.push({ marker, active: false, experience });
      }
      markerBounds.extend(latLng);
      index++;
    }
    map.fitBounds(markerBounds);
  }

  private isOverlapping(overlap: number): boolean {
    return overlap >= 0;
  }

  private isMarginBiggerThanOverlap(overlap: number, margin: number): boolean {
    return overlap < margin;
  }

  private async newPositionOverlaps(
    overlaySize: number,
    bounds: google.maps.LatLngBounds,
    map: google.maps.Map,
    isWidth = true,
  ): Promise<boolean> {
    let newMargin: number;
    if (isWidth) {
      newMargin = (await this.googleMapsService.distanceToTopLeftInPx(bounds.getSouthWest(), map)).x;
    } else {
      const { y: distanceToBottom } = await this.googleMapsService.distanceToTopLeftInPx(
        this.map.getBounds().getSouthWest(),
        map,
      );
      const { y: distanceToMarkersBoundBottom } = await this.googleMapsService.distanceToTopLeftInPx(
        this.markerBounds.getSouthWest(),
        this.map,
      );
      newMargin = distanceToBottom - distanceToMarkersBoundBottom;
    }
    return newMargin <= overlaySize;
  }

  private async createActiveMarker(
    experience: ILocation,
    index: number,
    map: google.maps.Map,
  ): Promise<google.maps.marker.AdvancedMarkerElement> {
    const markerSize = this.isMobile ? { x: 60, y: 60 } : { x: 80, y: 80 };
    const latLng = await this.googleMapsService.getLatLng(experience.location.latitude, experience.location.longitude);
    return await this.googleMapsService.addMarker({
      position: latLng,
      title: experience.title,
      map,
      content: this.mapService.getMarkerPinElementFromImageUrl(
        this.mapService.getNumberedImageUrl(index, this.ACTIVE_MARKER_URL),
        markerSize,
        experience.title,
      ),
    });
  }

  private async createNormalMarker(
    experience: ILocation,
    index: number,
    map: google.maps.Map,
  ): Promise<google.maps.marker.AdvancedMarkerElement> {
    const latLng = await this.googleMapsService.getLatLng(experience.location.latitude, experience.location.longitude);
    const markerSize = this.isMobile ? { x: 25, y: 25 } : { x: 30, y: 30 };
    const marker = await this.googleMapsService.addMarker({
      position: latLng,
      title: experience.title,
      map,
      content: this.mapService.getMarkerPinElementFromImageUrl(
        this.mapService.getNumberedImageUrl(index, this.MARKER_URL),
        markerSize,
        experience.title,
      ),
    });
    if (this.markerClickFunction) {
      marker.addListener('click', () => {
        this.changeActiveMarker(index);
        this.markerClickFunction(index);
      });
    }
    return marker;
  }
}
